/**
 * Cookie Consent Manager
 * Gère le consentement des cookies avec persistence localStorage
 * Compatible avec l'API Shopify Customer Privacy et Facebook Pixel
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'beldyness_cookie_consent';
  const CONSENT_DURATION_DAYS = 365;

  /**
   * Gestionnaire de consentement des cookies
   */
  class CookieConsentManager {
    constructor() {
      this.consent = this.loadConsent();
      this.initializeTracking();
    }

    /**
     * Charge le consentement depuis localStorage
     */
    loadConsent() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return null;

        const data = JSON.parse(stored);
        const now = new Date().getTime();

        // Vérifier si le consentement a expiré
        if (data.expiresAt && now > data.expiresAt) {
          localStorage.removeItem(STORAGE_KEY);
          return null;
        }

        return data;
      } catch (error) {
        console.error('Erreur lors du chargement du consentement:', error);
        return null;
      }
    }

    /**
     * Sauvegarde le consentement dans localStorage
     */
    saveConsent(accepted) {
      try {
        const now = new Date();
        const expiresAt = new Date(now.getTime() + (CONSENT_DURATION_DAYS * 24 * 60 * 60 * 1000));

        const data = {
          accepted: accepted,
          timestamp: now.getTime(),
          expiresAt: expiresAt.getTime(),
          version: '1.0'
        };

        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        this.consent = data;

        console.log(`Consentement des cookies ${accepted ? 'accepté' : 'refusé'} jusqu'au`, expiresAt.toLocaleDateString());
      } catch (error) {
        console.error('Erreur lors de la sauvegarde du consentement:', error);
      }
    }

    /**
     * Vérifie si la bannière doit être affichée
     */
    shouldShowBanner() {
      return this.consent === null;
    }

    /**
     * Accepte les cookies
     */
    acceptCookies() {
      this.saveConsent(true);
      this.enableTracking();

      // Compatibilité avec l'API Shopify
      if (window.Shopify?.customerPrivacy?.setTrackingConsent) {
        window.Shopify.customerPrivacy.setTrackingConsent(true, () => {
          console.log('Consentement Shopify accepté');
        });
      }

      return true;
    }

    /**
     * Refuse les cookies
     */
    declineCookies() {
      this.saveConsent(false);
      this.disableTracking();

      // Compatibilité avec l'API Shopify
      if (window.Shopify?.customerPrivacy?.setTrackingConsent) {
        window.Shopify.customerPrivacy.setTrackingConsent(false, () => {
          console.log('Consentement Shopify refusé');
        });
      }

      return true;
    }

    /**
     * Active le tracking (Facebook Pixel, etc.)
     */
    enableTracking() {
      console.log('Tracking activé');

      // Activer Facebook Pixel si disponible
      if (window.fbq) {
        try {
          window.fbq('consent', 'grant');
          console.log('Facebook Pixel: consentement accordé');
        } catch (error) {
          console.error('Erreur Facebook Pixel:', error);
        }
      }

      // Déclencher un événement personnalisé pour d'autres scripts
      window.dispatchEvent(new CustomEvent('cookieConsentGranted', {
        detail: { timestamp: new Date().getTime() }
      }));
    }

    /**
     * Désactive le tracking
     */
    disableTracking() {
      console.log('Tracking désactivé');

      // Désactiver Facebook Pixel si disponible
      if (window.fbq) {
        try {
          window.fbq('consent', 'revoke');
          console.log('Facebook Pixel: consentement révoqué');
        } catch (error) {
          console.error('Erreur Facebook Pixel:', error);
        }
      }

      // Déclencher un événement personnalisé
      window.dispatchEvent(new CustomEvent('cookieConsentRevoked', {
        detail: { timestamp: new Date().getTime() }
      }));
    }

    /**
     * Initialise le tracking basé sur le consentement existant
     */
    initializeTracking() {
      if (this.consent && this.consent.accepted) {
        this.enableTracking();
      } else if (this.consent && !this.consent.accepted) {
        this.disableTracking();
      }
    }

    /**
     * Réinitialise le consentement (pour debug/test)
     */
    resetConsent() {
      localStorage.removeItem(STORAGE_KEY);
      this.consent = null;
      console.log('Consentement réinitialisé');
    }
  }

  // Créer l'instance globale
  window.cookieConsentManager = new CookieConsentManager();

  // Si le consentement existe déjà, masquer immédiatement la bannière avec CSS
  if (!window.cookieConsentManager.shouldShowBanner()) {
    console.log('Consentement existant détecté - Masquage de la bannière');
    var style = document.createElement('style');
    style.textContent = 'privacy-banner { display: none !important; visibility: hidden !important; }';
    document.head.appendChild(style);

    // Observer pour empêcher la bannière de se réafficher
    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          var banner = mutation.target;
          if (banner.tagName.toLowerCase() === 'privacy-banner') {
            banner.style.display = 'none';
            banner.style.visibility = 'hidden';
          }
        }
      });
    });

    // Observer le DOM pour masquer la bannière dès qu'elle apparaît
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        var banner = document.querySelector('privacy-banner');
        if (banner) {
          banner.style.display = 'none';
          banner.style.visibility = 'hidden';
          observer.observe(banner, { attributes: true, attributeFilter: ['style'] });
        }
      });
    } else {
      var banner = document.querySelector('privacy-banner');
      if (banner) {
        banner.style.display = 'none';
        banner.style.visibility = 'hidden';
        observer.observe(banner, { attributes: true, attributeFilter: ['style'] });
      }
    }
  }

  /**
   * Override de la classe PrivacyBanner pour utiliser notre gestionnaire
   */
  function enhancePrivacyBanner() {
    const privacyBanner = document.querySelector('privacy-banner');
    if (!privacyBanner) return;

    // Vérifier si on doit afficher la bannière
    if (!window.cookieConsentManager.shouldShowBanner()) {
      // Ne pas afficher la bannière si le consentement existe déjà
      console.log('Bannière de cookies masquée: consentement existant');
      return;
    }

    // Attendre que le custom element soit défini
    if (window.customElements.get('privacy-banner')) {
      // Intercepter les clics sur les boutons
      const acceptButton = privacyBanner.querySelector('[data-action="accept"]');
      const declineButton = privacyBanner.querySelector('[data-action="decline"]');

      if (acceptButton) {
        acceptButton.addEventListener('click', (e) => {
          e.stopImmediatePropagation();
          window.cookieConsentManager.acceptCookies();
          privacyBanner.hide?.();

          // Si la méthode hide n'existe pas, masquer manuellement
          if (!privacyBanner.hide) {
            privacyBanner.style.display = 'none';
          }
        }, { capture: true });
      }

      if (declineButton) {
        declineButton.addEventListener('click', (e) => {
          e.stopImmediatePropagation();
          window.cookieConsentManager.declineCookies();
          privacyBanner.hide?.();

          // Si la méthode hide n'existe pas, masquer manuellement
          if (!privacyBanner.hide) {
            privacyBanner.style.display = 'none';
          }
        }, { capture: true });
      }
    }
  }

  // Initialiser quand le DOM est prêt
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhancePrivacyBanner);
  } else {
    enhancePrivacyBanner();
  }

  // Override FORCÉ de l'API Shopify (AVANT que theme.js se charge)
  window.Shopify = window.Shopify || {};
  window.Shopify.customerPrivacy = {
    shouldShowBanner: () => {
      const shouldShow = window.cookieConsentManager.shouldShowBanner();
      console.log('Shopify.customerPrivacy.shouldShowBanner() =', shouldShow);
      return shouldShow;
    },
    setTrackingConsent: (consent, callback) => {
      console.log('Shopify.customerPrivacy.setTrackingConsent()', consent);
      if (consent) {
        window.cookieConsentManager.acceptCookies();
      } else {
        window.cookieConsentManager.declineCookies();
      }
      if (callback) callback();
    }
  };

  // Forcer le chargement de l'API pour éviter que Shopify la recharge
  window.Shopify.loadFeatures = window.Shopify.loadFeatures || function(features) {
    features.forEach(function(feature) {
      if (feature.name === 'consent-tracking-api' && feature.onLoad) {
        // Appeler immédiatement onLoad avec notre API mockée
        setTimeout(feature.onLoad, 0);
      }
    });
  };

  // Exposer des méthodes utiles pour le debug
  window.debugCookieConsent = {
    reset: () => window.cookieConsentManager.resetConsent(),
    status: () => window.cookieConsentManager.consent,
    accept: () => window.cookieConsentManager.acceptCookies(),
    decline: () => window.cookieConsentManager.declineCookies()
  };

  console.log('Cookie Consent Manager initialisé');
  console.log('Pour debug: window.debugCookieConsent');
})();
